// The Integrations & API page's logic, with no React in it.
//
// Components have no coverage in this repository — there is no DOM harness, and the node
// test runner cannot import `.jsx` at all — so everything decidable without a DOM is
// decided here and tested under `node --test`, the shape descriptor-form.js established
// (and web/src/components/create/custom-field-values.js before it).
//
// WHY THIS IS NOT descriptor-form.js. A descriptor is a FLAT row: its fields are columns
// and `changedValues` diffs them one level deep. An integration is three things at once —
// a name, a nested `config` object the server merges KEY BY KEY, and a `secrets` object
// that can only be written and never read back. The third is what makes a shared module
// impossible: `changedValues` treats "the control is empty" as a value, and for a
// write-only credential that is the one reading that destroys data.
//
// THE RULE THE WHOLE FILE IS ARRANGED AROUND, stated once here because everything below
// follows from it:
//
//   A secret box on this page is ALWAYS EMPTY when the screen opens. It has to be — the
//   API masks the stored value to `{ set: true|false|null }` and there is no endpoint
//   that returns the value (src/services/admin/integrations.js). So "I did not touch
//   this" and "erase this" look identical on screen, and must not look identical on the
//   wire:
//
//     ''    omitted from the PATCH  -> the stored credential is untouched
//     null  sent as null            -> the stored credential is erased
//     'xyz' sent as 'xyz'           -> replaced
//
//   The page offers an explicit Clear control for the middle case; nothing else can
//   produce it. A form that posted the empty box as the value would wipe every
//   credential on the row the first time an admin renamed it.

/** The path `src/routes/zendesk.js` mounts the receiver on. One literal, two readers. */
export const WEBHOOK_PATH = '/zendesk/events';

const isPlainObject = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * The empty a config control starts at, by the registry's own field vocabulary
 * (`FIELD_TYPES` in src/integrations/registry.js): `string` and `enum` are text, `json`
 * is an object. Never `undefined` — an uncontrolled input that becomes controlled on the
 * first keystroke is a React warning and a lost character.
 */
function emptyFor(field) {
  return field?.type === 'json' ? {} : '';
}

/** Structural equality, enough for the strings and small maps a config holds. */
function same(a, b) {
  if (isPlainObject(a) || isPlainObject(b) || Array.isArray(a) || Array.isArray(b)) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return a === b;
}

/**
 * What the edit form starts with, for a kind and either a stored row or `null` (create).
 *
 * `kind` is a `kindSummaries()` entry off `GET /api/admin/integrations` — the page draws
 * a form for a kind it has never heard of from that alone, which is the whole point of
 * the registry. A missing or unknown kind yields an empty form rather than a throw: a
 * row whose kind was removed from the registry still needs the screen an admin would
 * use to delete it.
 *
 * @param {{config?: object[], secrets?: object[]}|null} kind
 * @param {object|null} row  null when creating.
 */
export function initialIntegrationValues(kind, row) {
  const creating = row === null || row === undefined;
  const config = {};
  for (const field of kind?.config ?? []) {
    const current = creating ? undefined : row.config?.[field.key];
    if (current !== undefined && current !== null) {
      config[field.key] = current;
      continue;
    }
    // A declared `default` is what the server writes when the key is absent
    // (`defaultConfigFor`), so a create starts there rather than at the type's empty.
    // On an EDIT a declared default is used too, and for a different reason: the field
    // is absent from the stored config, so starting at the default renders what the
    // server would use rather than a blank that reads as "nothing configured".
    config[field.key] = Object.hasOwn(field, 'default')
      ? structuredClone(field.default)
      : emptyFor(field);
  }

  // Deliberately NOT seeded from `row.secrets`. That is the mask — `{ set: true }` — not
  // a value, and putting it in a box an admin could submit is how the literal string
  // "[object Object]" becomes an API token.
  const secrets = {};
  for (const field of kind?.secrets ?? []) secrets[field.key] = '';

  return {
    name: creating ? '' : (row.name ?? ''),
    is_enabled: creating ? false : row.is_enabled === true,
    config,
    secrets,
  };
}

/**
 * The request body: everything on a create, only what changed on an edit.
 *
 * CREATE posts `{ kind, name, config, secrets }` — `POST /api/admin/integrations` takes
 * no `is_enabled` (a row cannot be born enabled), so none is offered.
 *
 * EDIT is a PATCH, and "key absent = unchanged" is the contract (docs/API.md §3.4).
 * `config` carries ONLY the keys that moved, because `updateIntegration` merges key by
 * key: a form that posted the whole object would rewrite fields nobody touched and put
 * changes nobody made into every `config_events` diff. It also means a config key this
 * page never rendered — one a newer server added — survives an edit by an older page.
 *
 * @param {{kind?: string, config?: object[], secrets?: object[]}|null} kind
 * @param {{name: string, is_enabled: boolean, config: object, secrets: object}} values
 * @param {object|null} row
 */
export function changedIntegrationValues(kind, values, row) {
  const creating = row === null || row === undefined;
  const fields = kind?.config ?? [];

  const secrets = {};
  for (const field of kind?.secrets ?? []) {
    const typed = values.secrets?.[field.key];
    // See the header: '' and undefined are "untouched", and untouched is ABSENT.
    if (typed === '' || typed === undefined) continue;
    // A clear has nothing to erase on a create, and `null` there would be sealed as a
    // null credential rather than as no credential at all.
    if (typed === null) {
      if (!creating) secrets[field.key] = null;
      continue;
    }
    secrets[field.key] = typed;
  }

  if (creating) {
    const config = {};
    for (const field of fields) config[field.key] = values.config?.[field.key];
    return { kind: kind?.kind, name: values.name, config, secrets };
  }

  const body = {};
  if (values.name !== row.name) body.name = values.name;

  const config = {};
  for (const field of fields) {
    const was = row.config?.[field.key] ?? emptyFor(field);
    const now = values.config?.[field.key];
    if (!same(now, was)) config[field.key] = now;
  }
  if (Object.keys(config).length > 0) body.config = config;

  if (Object.keys(secrets).length > 0) body.secrets = secrets;
  if ((values.is_enabled === true) !== (row.is_enabled === true)) {
    body.is_enabled = values.is_enabled === true;
  }
  return body;
}

/** A count that is actually a count, and actually worth printing. */
const pending = (value) => (Number.isInteger(value) && value > 0 ? value : null);

/**
 * The one-line answer to "is this thing working?", from `status_json`.
 *
 * `status_json` is HEALTH, never configuration (spec §11.1): the integration writes it
 * about itself and the console only reads it. Its column CHECK is one-argument `ISJSON`,
 * so a row can legally hold `[]` or `7`, and the service degrades either to `{}` — but
 * this function is also handed whatever a future task decides to write, so every read
 * below is type-checked rather than trusted. A malformed status must not take down the
 * list an admin would use to fix it.
 *
 * `last_error` comes back SEPARATELY rather than appended to the sentence, so the page
 * can colour it without parsing its own output back apart.
 *
 * @param {object} status
 * @param {(iso: string) => string|null} formatTime  Injected so this stays deterministic
 *   under test; the page passes `formatDateTime`.
 * @returns {{text: string|null, error: string|null}}
 */
export function healthLine(status, formatTime = (iso) => iso) {
  const health = isPlainObject(status) ? status : {};
  const parts = [];

  // Backlog first: it is the number that means something is wrong RIGHT NOW, where a
  // timestamp only says when something last went right. Zero is the healthy state and is
  // left out — it would be noise beside four other facts.
  const inbox = pending(health.pending_inbox);
  if (inbox !== null) parts.push(`${inbox} waiting to import`);
  const outbox = pending(health.pending_outbox);
  if (outbox !== null) parts.push(`${outbox} waiting to send`);

  for (const [key, label] of [['last_webhook_at', 'Last webhook'], ['last_sweep_at', 'Last sweep']]) {
    if (typeof health[key] !== 'string') continue;
    const when = formatTime(health[key]);
    if (when) parts.push(`${label} ${when}`);
  }

  const error = health.last_error === null || health.last_error === undefined
    ? null
    : String(health.last_error);

  return { text: parts.length > 0 ? parts.join(' · ') : null, error };
}

/**
 * The Zendesk-side setup an admin has to do BY HAND, in Admin Center (spec §6).
 *
 * Read-only on purpose: nothing here is stored and nothing here is a setting. It exists
 * because the webhook URL depends on where this app is deployed (ngrok in dev, the App
 * Service later) and the trigger bodies are exact — `readZendeskId` in
 * src/routes/zendesk.js reads `zendesk_id` and NOTHING else, deliberately, so that a
 * misconfigured trigger fails loudly instead of quietly working. A body retyped from
 * memory into Zendesk is the failure this panel exists to prevent.
 *
 * @param {string} origin  `window.location.origin`.
 */
export function triggerBodies(origin) {
  const base = String(origin ?? '').replace(/\/+$/, '');
  return {
    url: `${base}${WEBHOOK_PATH}`,
    triggers: [
      {
        event: 'created',
        title: 'CRM bridge — ticket created',
        when: 'Ticket is Created',
        body: '{ "zendesk_id": "{{ticket.id}}", "event": "created" }',
      },
      {
        event: 'updated',
        title: 'CRM bridge — ticket updated',
        when: 'Ticket is Updated',
        body: '{ "zendesk_id": "{{ticket.id}}", "event": "updated" }',
      },
    ],
  };
}

/**
 * A `type: 'json'` config field's textarea, read as a value.
 *
 * The failure that matters is the SILENT one, exactly as in `parseActionsDraft`: text the
 * editor cannot read must BLOCK the save, never resolve to `{}`. A group→queue map that
 * round-trips to empty because a textarea could not parse it is data loss with a success
 * message on top of it — and the consequence here is every Zendesk group landing in the
 * default queue.
 *
 * Blank IS a value (`{}` — "no mapping"), because that is what the registry declares as
 * the default and an admin must be able to get back to it.
 *
 * @returns {{ok: true, value: object, error: null}|{ok: false, value: null, error: string}}
 */
export function parseJsonObjectDraft(text) {
  const trimmed = String(text ?? '').trim();
  if (trimmed === '') return { ok: true, value: {}, error: null };

  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch (err) {
    return { ok: false, value: null, error: `Not valid JSON: ${err.message}` };
  }
  if (!isPlainObject(parsed)) {
    return { ok: false, value: null, error: 'Must be a JSON object, like {"360001234": "3"}.' };
  }
  return { ok: true, value: parsed, error: null };
}

/**
 * The banner above the list when credentials cannot be stored — or `null`.
 *
 * TWO FAULTS, TWO FIXES, and collapsing them sends an admin to add a variable that is
 * already there:
 *
 *   `configured: false, error: null`       INTEGRATION_SECRETS_KEY is not set.
 *   `configured: false, error: 'BAD_KEY'`  it is set and does not decode to 32 bytes.
 *
 * Both say what still works, because most of the page does: the API resolves a key only
 * on the branch that has a credential to seal, so renaming, editing config and
 * DISABLING all succeed whatever state the variable is in. That last one is not a
 * convenience — `deleteIntegration` refuses while enabled, so if a broken key could
 * refuse the disable, the row would be unreachable by every route that could fix it.
 *
 * @param {{secrets_key_configured?: boolean, secrets_key_error?: string|null}} meta
 */
export function secretsKeyBanner(meta) {
  if (meta?.secrets_key_configured !== false) return null;

  const stillWorks = 'Everything else on this page still works: names, configuration, '
    + 'and disabling an integration.';

  if (meta.secrets_key_error === 'BAD_KEY') {
    return {
      headline: 'INTEGRATION_SECRETS_KEY is set but does not decode to 32 bytes, so credentials '
        + 'cannot be saved and the ones already stored cannot be read.',
      detail: 'Replace it in .env with the output of '
        + 'node -e "console.log(require(\'crypto\').randomBytes(32).toString(\'base64\'))" — and note '
        + 'that a new key cannot open credentials sealed with the old one; those must be '
        + `re-entered. ${stillWorks}`,
    };
  }

  return {
    headline: 'INTEGRATION_SECRETS_KEY is not set on the server, so credentials cannot be saved.',
    detail: 'Generate one with '
      + 'node -e "console.log(require(\'crypto\').randomBytes(32).toString(\'base64\'))" '
      + `and add it to .env, then restart the server. ${stillWorks}`,
  };
}

/**
 * What to print beside a write-only field, from the mask the API sends.
 *
 * THREE STATES, NOT TWO, and the third is the reason this function exists. `{ set: null }`
 * means a sealed value IS stored and this process cannot open it — no key, the wrong key,
 * or a corrupted column. An admin told "Not set" there would retype a credential to fix a
 * problem that does not exist, or conclude the row is empty and delete it. Anything
 * unrecognised falls to the same honest "I cannot tell", never to "not set".
 */
export function secretStateLabel(mask) {
  if (mask?.set === true) return 'Set';
  if (mask?.set === false) return 'Not set';
  return 'Set, but unreadable with the current key';
}
